import { OFFLINE_ENABLED } from './entities';
import { db, ALL_TABLES } from './db';
import { newOpId, cascadeDiscardChildren } from './opQueue';
import {
  isConnectivityError,
  isEffectivelyOnline,
  reportConnectivitySuccess,
  reportFromError,
} from './connectivity';

/**
 * Thrown when an offline write is blocked because the device is out of storage
 * (a genuine QuotaExceededError mid-transaction). The persist() durability
 * check is advisory-only and never blocks a write — it drives the warning
 * banner instead. Also reused as Billing's intentional online-only sentinel.
 */
export class OfflineWriteBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OfflineWriteBlockedError';
  }
}

/** True for an IndexedDB quota error — raw (`name`) or Dexie-wrapped inside a
 *  transaction (`inner.name`). (B6) */
function isQuotaError(e: any): boolean {
  return e?.name === 'QuotaExceededError' || e?.inner?.name === 'QuotaExceededError';
}

// Connectivity classification lives in ./connectivity (shared with the read
// path and the provider): only a no-response failure falls through to the
// offline queue; a validation/auth error must still surface.

/** Notify the OfflineProvider (same tab) that the queue changed, so the unsynced /
 *  needs-review counts refresh IMMEDIATELY after an offline write. Without this the
 *  "N unsynced" badge doesn't update until the next sync / visibility / online
 *  event — making a just-queued offline write look like it did nothing. */
function notifyQueueChanged(): void {
  try {
    window.dispatchEvent(new Event('st-offline-queue-changed'));
  } catch {
    /* non-browser context — no-op */
  }
}

/**
 * Run an offline write transaction, mapping an IndexedDB quota failure to a
 * typed OfflineWriteBlockedError (so the existing block UI handles it) while
 * letting any other error propagate raw. The transaction is atomic, so a
 * quota-failed write rolls back both the op and the optimistic row. (B6)
 */
async function runOfflineTx(fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn();
  } catch (e) {
    if (isQuotaError(e)) {
      throw new OfflineWriteBlockedError(
        "Can't save offline — this device is out of storage. Reconnect to save this change.",
      );
    }
    throw e;
  }
}

/**
 * Durability policy (revised 2026-06-09): offline writes are NO LONGER gated on
 * `navigator.storage.persist()`. That permission is a browser HEURISTIC — Chrome/
 * Edge grant it only to installed PWAs with enough engagement, and refuse it even
 * for a correctly-installed app on some setups. Hard-blocking on it meant a user
 * whose browser declined the grant could not save offline AT ALL, with no way to
 * fix it themselves — the worst outcome for a field vet.
 *
 * Instead, `persist()` is requested opportunistically (OfflineProvider on mount)
 * and is purely ADVISORY: `persisted === false` drives a loud "not fully secured —
 * sync as soon as you reconnect" warning (OfflineIndicator), but never blocks a
 * write. The genuine eviction trigger is storage PRESSURE, and that is still
 * hard-blocked: runOfflineTx maps a real QuotaExceededError (out of space) to
 * OfflineWriteBlockedError below. Combined with never-delete-before-ack, an
 * un-synced write is never dropped on OUR side; the residual risk (the browser
 * evicting evictable storage under disk pressure before a sync) is small and
 * mitigated by the warning + aggressive sync-on-reconnect.
 */

export interface OfflineWriteParams<T> {
  /** synced entityType, e.g. 'medication' */
  entityType: string;
  /** replica table the optimistic row lives in, e.g. 'medications' */
  table: string;
  /** the create payload (what the service would POST); must include horseId */
  payload: Record<string, any>;
  /** the existing online network call */
  networkFn: () => Promise<T>;
  /** extra fields to merge into the optimistic row (e.g. clinicId) */
  extra?: Record<string, any>;
  /**
   * B13: author identity stamped onto the OPTIMISTIC ROW ONLY (so the chart
   * shows "who entered this" immediately while offline). NEVER merged into the
   * queued op payload — the server stamps the authoritative author from the JWT
   * at push, and a stray field would risk a Prisma "Unknown argument" reject.
   */
  author?: { providerId?: string; createdBy?: string; provider?: { id: string; name?: string } };
}

/**
 * Create-path write with offline support (the write-side sibling of
 * withOfflineFallback). Online — or when the flag is off — runs the existing
 * network call unchanged (zero behavior change). Offline, it queues an op and
 * optimistically writes the row into the replica so reads (which fall back to
 * the replica) show it immediately; the op is pushed via /sync/push on
 * reconnect. Returns the created record (optimistic row when offline).
 */
export async function executeWrite<T>(params: OfflineWriteParams<T>): Promise<T> {
  const { entityType, table, payload, networkFn, extra, author } = params;

  if (!OFFLINE_ENABLED) return networkFn();
  if (isEffectivelyOnline()) {
    try {
      const res = await networkFn();
      reportConnectivitySuccess();
      return res;
    } catch (e) {
      // Real server reject (4xx/5xx) → surface it (and it proves reachability).
      // Connectivity failure (no response) → record it and fall through to the
      // offline queue, so a write isn't lost just because navigator.onLine lied
      // about being online. Once observed-down, later writes queue immediately
      // (no doomed network attempt); the provider's probe flips us back.
      reportFromError(e);
      if (!isConnectivityError(e)) throw e;
    }
  }

  const tempId = newOpId();
  const clientTimestamp = new Date().toISOString();
  const optimistic = {
    ...payload,
    ...extra,
    ...(author ?? {}), // B13: author on the OPTIMISTIC ROW only, never the op payload
    id: tempId,
    createdAt: clientTimestamp,
    updatedAt: clientTimestamp,
    _pendingSync: true,
  };

  // Op + optimistic row written atomically — a reader can never see one
  // without the other, and the op is never lost before its server ack.
  await runOfflineTx(() =>
    db.transaction('rw', db.opQueue, (db as any)[table], async () => {
      await db.opQueue.put({
        id: tempId,
        entityType,
        operationType: 'create',
        entityId: tempId,
        payload,
        clientTimestamp,
        table,
        status: 'pending',
      });
      await (db as any)[table].put(optimistic);
    }),
  );

  notifyQueueChanged();
  return optimistic as unknown as T;
}

export interface OfflineUpdateParams<T> {
  entityType: string;
  table: string;
  /** real server id, OR the temp id of a not-yet-synced offline-created row */
  entityId: string;
  payload: Record<string, any>;
  networkFn: () => Promise<T>;
}

/**
 * Update-path write with offline support. Online/flag-off → networkFn(). Offline
 * → optimistically merge `payload` into the replica row and queue the change,
 * with coalescing: editing a not-yet-synced offline create folds into that
 * create op (no stranded update against a temp id); repeated edits merge into a
 * single pending update op.
 */
export async function executeUpdate<T>(params: OfflineUpdateParams<T>): Promise<T> {
  const { entityType, table, entityId, payload, networkFn } = params;

  if (!OFFLINE_ENABLED) return networkFn();
  if (isEffectivelyOnline()) {
    try {
      const res = await networkFn();
      reportConnectivitySuccess();
      return res;
    } catch (e) {
      // Real server reject (4xx/5xx) → surface it (and it proves reachability).
      // Connectivity failure (no response) → record it and fall through to the
      // offline queue, so a write isn't lost just because navigator.onLine lied
      // about being online. Once observed-down, later writes queue immediately
      // (no doomed network attempt); the provider's probe flips us back.
      reportFromError(e);
      if (!isConnectivityError(e)) throw e;
    }
  }

  const clientTimestamp = new Date().toISOString();
  let resultRow: any;
  await runOfflineTx(() =>
    db.transaction('rw', db.opQueue, (db as any)[table], async () => {
    // Consider ALL active ops for this entity (not just 'pending'), so
    // re-editing a parked conflict/error op folds into + re-submits it rather
    // than piling up a second op for the same record.
    const existing = await db.opQueue.filter((o) => o.entityId === entityId).toArray();
    const createOp = existing.find((o) => o.operationType === 'create');
    const updateOp = existing.find((o) => o.operationType === 'update');

    // Base version for clock-skew-immune conflict detection (B2): the last
    // server-acknowledged updatedAt. A CLEAN replica row mirrors the server, so
    // its updatedAt IS the base. A _pendingSync row is the user's own un-pushed
    // optimistic state (updatedAt = a local clientTimestamp, NOT a server
    // version) → fall back to whatever base the op already carries, because the
    // server still hasn't seen any of these edits.
    const current = await (db as any)[table].get(entityId);
    const cleanBase =
      current && !current._pendingSync ? current.updatedAt : undefined;

    if (createOp) {
      // Editing a not-yet-synced create — fold into its payload; keep it pending.
      // (No base: a create has nothing on the server to conflict against.)
      await db.opQueue.put({
        ...createOp,
        payload: { ...createOp.payload, ...payload },
        status: 'pending',
        message: undefined,
        retries: 0,
      });
    } else if (updateOp) {
      // Coalesce into the existing update op and re-submit it (clears a parked
      // conflict/error so the user's newest edit is retried — no pile-up).
      // Re-base only off a clean row (e.g. after a conflict reverted the replica
      // to the server version); while still pending, keep the original base.
      await db.opQueue.put({
        ...updateOp,
        payload: { ...updateOp.payload, ...payload },
        clientTimestamp,
        baseUpdatedAt: cleanBase ?? updateOp.baseUpdatedAt,
        status: 'pending',
        message: undefined,
        serverRecord: undefined,
        retries: 0,
      });
    } else {
      await db.opQueue.put({
        id: newOpId(),
        entityType,
        operationType: 'update',
        entityId,
        payload,
        clientTimestamp,
        baseUpdatedAt: cleanBase,
        table,
        status: 'pending',
      });
    }

    resultRow = { ...(current || { id: entityId }), ...payload, updatedAt: clientTimestamp, _pendingSync: true };
    await (db as any)[table].put(resultRow);
    }),
  );

  notifyQueueChanged();
  return resultRow as T;
}

export interface OfflineDeleteParams<T> {
  entityType: string;
  table: string;
  entityId: string;
  networkFn: () => Promise<T>;
}

/**
 * Delete-path write with offline support. Online/flag-off → networkFn(). Offline
 * → optimistically remove the replica row and queue a delete, with coalescing:
 * deleting a not-yet-synced offline create cancels it entirely (no server op);
 * otherwise the prior row is backed up on the op so a failed/conflicted delete
 * restores it.
 */
export async function executeDelete<T>(params: OfflineDeleteParams<T>): Promise<T> {
  const { entityType, table, entityId, networkFn } = params;

  if (!OFFLINE_ENABLED) return networkFn();
  if (isEffectivelyOnline()) {
    try {
      const res = await networkFn();
      reportConnectivitySuccess();
      return res;
    } catch (e) {
      // Real server reject (4xx/5xx) → surface it (and it proves reachability).
      // Connectivity failure (no response) → record it and fall through to the
      // offline queue, so a write isn't lost just because navigator.onLine lied
      // about being online. Once observed-down, later writes queue immediately
      // (no doomed network attempt); the provider's probe flips us back.
      reportFromError(e);
      if (!isConnectivityError(e)) throw e;
    }
  }

  await runOfflineTx(() =>
    db.transaction('rw', ALL_TABLES.map((t) => (db as any)[t]), async () => {
    // Consider ALL active ops for this entity so a delete supersedes parked
    // (conflict/error) updates too, not just pending ones.
    const existing = await db.opQueue.filter((o) => o.entityId === entityId).toArray();
    const createOp = existing.find((o) => o.operationType === 'create');
    const updateOps = existing.filter((o) => o.operationType === 'update');

    // Supersede any queued updates for this entity either way.
    for (const u of updateOps) await db.opQueue.delete(u.id);

    if (createOp) {
      // Deleting a not-yet-synced create — cancel entirely; nothing to sync.
      // B8: also cancel child creates that reference this temp id, so none is
      // left pointing at a parent that never reaches the server.
      await cascadeDiscardChildren(entityId);
      await db.opQueue.delete(createOp.id);
      await (db as any)[table].delete(entityId);
      return;
    }

    const backup = await (db as any)[table].get(entityId);
    await db.opQueue.put({
      id: newOpId(),
      entityType,
      operationType: 'delete',
      entityId,
      payload: {},
      clientTimestamp: new Date().toISOString(),
      table,
      status: 'pending',
      serverRecord: backup, // restore source if the delete later fails/conflicts
    });
    await (db as any)[table].delete(entityId);
    }),
  );

  notifyQueueChanged();
  return undefined as unknown as T;
}
